import * as Icons from '@phosphor-icons/react';
import { useMemo } from 'react';
import Select, { components, type OptionProps, type SingleValueProps } from 'react-select';
import { getSharedStyles } from '../../utils/select';

type IconPickerOption = {
    label: string;
    value: string;
    icon: React.ReactNode;
};

interface IconPickerProps {
    iconOptions?: Array<{
        value: string;
        label: string;
        icon: React.ReactNode;
    }>;
    selectedValue: string | null;
    onSelectionChange?: (selection: { value: string; label: string } | null) => void;
    placeholder: string;
    inputId: string;
    className?: string;
    optionWidth?: string; // Width of each option, defaults to "100%"
}

const Option = (props: OptionProps<IconPickerOption>) => {
    const { innerProps, ...restProps } = props;
    // Remove onMouseMove and onMouseOver to improve react-select performance
    const { onMouseMove: _onMouseMove, onMouseOver: _onMouseOver, ...restInnerProps } = innerProps;
    const editedProps = { ...restProps, innerProps: restInnerProps };

    return (
        <components.Option {...editedProps}>
            <span className="icon-option" title={props.label}>
                {props.data.icon}
                <span>{props.label}</span>
            </span>
        </components.Option>
    );
};

const SingleValue = (props: SingleValueProps<IconPickerOption>) => {
    return (
        <components.SingleValue className="icon-value" {...props}>
            {props.data.icon}
            <span>{props.data.label}</span>
        </components.SingleValue>
    );
};

const IconPicker = ({
    iconOptions,
    selectedValue,
    onSelectionChange,
    placeholder,
    inputId,
    className,
    optionWidth = '100%',
}: IconPickerProps) => {
    const phosphorOptions = useMemo(
        () =>
            (
                Object.entries(Icons).filter(([_, value]) => {
                    // @ts-expect-error Neither 'render' nor 'displayName' are part of any exported types
                    return value?.render && value?.displayName !== 'IconBase';
                }) as [string, Icons.Icon][]
            ).map(([key, IconComponent]) => ({
                label: key,
                value: key,
                icon: <IconComponent />,
            })),
        [],
    );

    const options = iconOptions || phosphorOptions;

    // Determine selected value
    const value = useMemo(() => {
        if (!selectedValue) return null;

        return (
            options.find(({ value }) => value.toUpperCase() === selectedValue.toUpperCase()) || null
        );
    }, [selectedValue, options]);

    const styles = getSharedStyles<IconPickerOption>({
        menuList: (baseStyles) => ({
            ...baseStyles,
            display: 'flex',
            flexWrap: 'wrap',
        }),
        option: (baseStyles) => ({
            ...baseStyles,
            width: optionWidth,
            padding: 0,
        }),
    });

    const handleChange = (option: IconPickerOption | null) => {
        if (!onSelectionChange) return;

        if (!option) {
            onSelectionChange(null);
            return;
        }

        onSelectionChange({ value: option.value, label: option.label });
    };

    return (
        <Select
            inputId={inputId}
            className={className || ''}
            isClearable={true}
            classNamePrefix="icon-picker"
            styles={styles}
            onChange={handleChange}
            placeholder={placeholder}
            value={value}
            options={options}
            components={{
                Option,
                SingleValue,
            }}
            menuPlacement="auto"
        />
    );
};

export default IconPicker;
